import { useCallback, useEffect, useState } from 'react'
import { fetchProducts } from '../api/catalog'
import { contractsApi, type AvailableContractOption, type ContractOption } from '../api/contracts'
import { errMsg } from '../lib/financeFormat'
import { Button, Callout, Card, CardContent, CardHeader, Select, useConfirm } from './ui'

function eur(n: number | null | undefined): string {
  return n == null ? '-' : n.toLocaleString('it-IT', { style: 'currency', currency: 'EUR' })
}

/**
 * Sezione "Opzioni e convergenze" della pratica: prodotti-opzione del catalogo che sommano il loro ricavo a quello del
 * prodotto principale (ricavo totale = prodotto + opzioni). Il genitore la monta solo per chi vede gli importi; lo staff
 * (`canManage`, pratica non annullata) vede anche la scelta "Aggiungi opzione" e la rimozione.
 */
export function ContractOptionsPanel({ contractId, productId, operatorId, canManage }: {
  contractId: number
  productId?: number | undefined
  operatorId?: number | null | undefined
  canManage: boolean
}) {
  const confirm = useConfirm()
  const [items, setItems] = useState<ContractOption[] | null>(null)
  const [available, setAvailable] = useState<AvailableContractOption[]>([])
  const [productRevenue, setProductRevenue] = useState<number | null>(null)
  const [choice, setChoice] = useState('')
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)

  const load = useCallback(async () => {
    try {
      setItems(await contractsApi.options(contractId))
      if (canManage) setAvailable(await contractsApi.availableOptions(contractId))
      setError('')
    } catch (e) {
      setError(errMsg(e, 'Impossibile caricare le opzioni.'))
    }
  }, [contractId, canManage])

  useEffect(() => { void load() }, [load])

  // Il ricavo del prodotto principale non e' nella pratica: si legge dal catalogo (piccolo), filtrato per operatore.
  useEffect(() => {
    if (!productId) { setProductRevenue(null); return }
    let alive = true
    fetchProducts(operatorId ? { operatorId, pageSize: 200 } : { pageSize: 200 })
      .then(r => { if (alive) setProductRevenue(r.items.find(p => p.id === productId)?.companyRevenue ?? null) })
      .catch(() => { if (alive) setProductRevenue(null) })
    return () => { alive = false }
  }, [productId, operatorId])

  const run = async (action: () => Promise<unknown>) => {
    setBusy(true)
    setError('')
    try {
      await action()
      await load()
    } catch (e) {
      setError(errMsg(e, 'Operazione non riuscita.'))
    } finally {
      setBusy(false)
    }
  }

  const add = () => {
    if (!choice) return
    void run(async () => {
      await contractsApi.addOption(contractId, { productId: Number(choice) })
      setChoice('')
    })
  }

  const remove = async (o: ContractOption) => {
    if (!(await confirm({ title: "Togliere l'opzione?", message: o.name, confirmLabel: 'Togli', destructive: true }))) return
    void run(() => contractsApi.removeOption(contractId, o.id))
  }

  if (items === null && !error) return null
  const list = items ?? []
  if (list.length === 0 && !canManage && !error) return null

  const optionsTotal = list.reduce((s, o) => s + (o.totalPrice ?? 0), 0)

  return (
    <Card variant="outlined" className="mb-4">
      <CardHeader icon="add_circle" title="Opzioni e convergenze" />
      <CardContent>
        {error && <Callout tone="error">{error}</Callout>}
        {list.length === 0 ? (
          <div className="md-typescale-body-medium">Nessuna opzione.</div>
        ) : (
          <ul className="list-unstyled mb-2">
            {list.map(o => (
              <li key={o.id} className="d-flex align-items-center justify-content-between gap-2 py-1">
                <span>{o.name}{o.quantity > 1 ? ` x${o.quantity}` : ''}</span>
                <span className="d-flex align-items-center gap-2">
                  <span>{eur(o.totalPrice)}</span>
                  {canManage && (
                    <Button type="button" variant="text" size="sm" disabled={busy} onClick={() => void remove(o)}>
                      Togli
                    </Button>
                  )}
                </span>
              </li>
            ))}
          </ul>
        )}
        {list.length > 0 && (
          <div className="md-typescale-body-medium"><strong>Totale opzioni:</strong> {eur(optionsTotal)}</div>
        )}
        <div className="md-typescale-body-medium mt-1">
          <strong>Ricavo totale:</strong>{' '}
          {productRevenue == null && list.length === 0 ? '-' : eur((productRevenue ?? 0) + optionsTotal)}
          {' '}(prodotto {eur(productRevenue)} + opzioni {eur(optionsTotal)})
        </div>
        {canManage && (
          <div className="mt-2 d-flex gap-2 align-items-end">
            <Select label="Aggiungi opzione" value={choice} onChange={e => setChoice(e.target.value)}>
              <option value="">Seleziona un'opzione…</option>
              {available.map(p => (
                <option key={p.id} value={p.id}>{p.name}{p.companyRevenue != null ? ` (${eur(p.companyRevenue)})` : ''}</option>
              ))}
            </Select>
            <Button type="button" variant="tonal" size="sm" disabled={busy || !choice} onClick={add}>Aggiungi opzione</Button>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
